import { z } from 'zod'
import { apiRequest } from '@/lib/api-client'

const workspaceRoleSchema = z.enum(['owner', 'admin', 'member', 'viewer'])

export const mcpClientVendorSchema = z.enum(['anthropic', 'openai', 'mistral', 'local', 'other'])
export type McpClientVendor = z.infer<typeof mcpClientVendorSchema>

const authorizeRequestSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  client_name: z.string(),
  redirect_host: z.string(),
  requested_scopes: z.array(z.string()),
  expires_at: z.string(),
  client_vendor: mcpClientVendorSchema,
  client_location: z.enum(['eu', 'usa', 'unknown']),
})

const consentWorkspaceSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: workspaceRoleSchema,
  grantable_scopes: z.array(z.string()),
})

const blockedWorkspaceSchema = z.object({
  id: z.string(),
  name: z.string(),
  reason: z.enum(['profile', 'provider_policy']),
})

const authorizeRequestResponseSchema = z.object({
  request: authorizeRequestSchema,
  workspaces: z.array(consentWorkspaceSchema),
  blocked_workspaces: z.array(blockedWorkspaceSchema),
})

const redirectResponseSchema = z.object({
  redirect_url: z.string(),
})

const connectionSchema = z.object({
  id: z.string(),
  client_id: z.string(),
  client_name: z.string(),
  scopes: z.array(z.string()),
  created_at: z.string(),
  last_used_at: z.string().nullable(),
  is_own: z.boolean(),
  can_revoke: z.boolean(),
})

const connectionsResponseSchema = z.object({
  can_manage_all: z.boolean(),
  connections: z.array(connectionSchema),
})

const revokeResponseSchema = z.object({
  revoked: z.boolean(),
})

const clientPolicySchema = z.object({
  allowed_clients: z.array(mcpClientVendorSchema),
  explicit: z.boolean(),
  eu_only: z.boolean(),
  can_edit: z.boolean(),
})

export type AuthorizeRequest = z.infer<typeof authorizeRequestSchema>
export type ConsentWorkspace = z.infer<typeof consentWorkspaceSchema>
export type BlockedWorkspace = z.infer<typeof blockedWorkspaceSchema>
export type AuthorizeRequestResponse = z.infer<typeof authorizeRequestResponseSchema>
export type McpConnection = z.infer<typeof connectionSchema>
export type ConnectionsResponse = z.infer<typeof connectionsResponseSchema>
export type McpClientPolicy = z.infer<typeof clientPolicySchema>

/** German labels and explanations for the MCP scopes MindKeep grants. */
export const MCP_SCOPE_LABELS: Record<string, { label: string; description: string }> = {
  'mindkeep:read': {
    label: 'Wissen lesen',
    description: 'Suchen und Quellen lesen in diesem Workspace.',
  },
  'mindkeep:inbox:write': {
    label: 'In die Inbox speichern',
    description:
      'Neue Notizen im Inbox-Ordner der App anlegen (z. B. _Inbox/Claude/). Bestehende Dateien werden nie überschrieben.',
  },
}

export function scopeLabel(scope: string): string {
  return MCP_SCOPE_LABELS[scope]?.label ?? scope
}

export function fetchAuthorizeRequest(id: string): Promise<AuthorizeRequestResponse> {
  return apiRequest({ path: `/mcp/authorize-requests/${encodeURIComponent(id)}`, schema: authorizeRequestResponseSchema })
}

export function approveAuthorizeRequest(
  id: string,
  workspaceId: string,
  scopes: string[],
): Promise<{ redirect_url: string }> {
  return apiRequest({
    method: 'POST',
    path: `/mcp/authorize-requests/${encodeURIComponent(id)}/approve`,
    body: { workspace_id: workspaceId, scopes },
    schema: redirectResponseSchema,
  })
}

export function denyAuthorizeRequest(id: string): Promise<{ redirect_url: string }> {
  return apiRequest({
    method: 'POST',
    path: `/mcp/authorize-requests/${encodeURIComponent(id)}/deny`,
    body: {},
    schema: redirectResponseSchema,
  })
}

export function fetchConnections(): Promise<ConnectionsResponse> {
  return apiRequest({ path: '/mcp/connections', schema: connectionsResponseSchema })
}

export function revokeConnection(id: string): Promise<{ revoked: boolean }> {
  return apiRequest({
    method: 'DELETE',
    path: `/mcp/connections/${encodeURIComponent(id)}`,
    schema: revokeResponseSchema,
  })
}

/** Vendor options in display order, with a flag for vendors hosted outside the EU. */
export const MCP_CLIENT_VENDOR_OPTIONS: Array<{ vendor: McpClientVendor; label: string; outsideEu: boolean }> = [
  { vendor: 'mistral', label: 'Mistral (EU)', outsideEu: false },
  { vendor: 'anthropic', label: 'Anthropic / Claude (USA)', outsideEu: true },
  { vendor: 'openai', label: 'OpenAI / ChatGPT (USA)', outsideEu: true },
  { vendor: 'local', label: 'Lokale Programme (CLI)', outsideEu: false },
  { vendor: 'other', label: 'Andere', outsideEu: false },
]

export function clientVendorDescription(vendor: McpClientVendor): string {
  switch (vendor) {
    case 'anthropic':
      return 'Anthropic, USA'
    case 'openai':
      return 'OpenAI, USA'
    case 'mistral':
      return 'Mistral, EU'
    case 'local':
      return 'lokales Programm, Anbieter nicht prüfbar'
    case 'other':
      return 'anderer Anbieter, nicht prüfbar'
  }
}

export function fetchMcpClientPolicy(): Promise<McpClientPolicy> {
  return apiRequest({ path: '/mcp/client-policy', schema: clientPolicySchema })
}

export function updateMcpClientPolicy(allowed: McpClientVendor[]): Promise<McpClientPolicy> {
  return apiRequest({
    method: 'PUT',
    path: '/mcp/client-policy',
    body: { allowed_clients: allowed },
    schema: clientPolicySchema,
  })
}
